import { isEmpty, isInstance, isNullish, notEmpty, recordValues } from "@de-xima/xima-common-js-lang";

/**
 * Attempts to locate the widget for an element, given the ID (not widgetVar) of the main DOM element of the widget (not the widget.jq.id)
 * @template {new (...args: never[]) => unknown} T Type of the widget to find.
 * @param {string} targetId ID of the DOM element of the widget.
 * @param {T} widgetClass Expected type of the widget. No check is performed if not given.
 * @returns {InstanceType<T> | undefined} The widget for the element, if found.
 */
export function findWidgetById(targetId, widgetClass) {
  if (isEmpty(targetId) || widgetClass === undefined) {
    return undefined;
  }
  for (const widgetVar of Object.keys(PrimeFaces.widgets)) {
    if (!(widgetVar in PrimeFaces.widgets)) {
      continue;
    }
    const widget = PrimeFaces.widgets[widgetVar];
    if (isNullish(widget)) {
      // Widget existed previously, but was removed via AJAX
      continue;
    }
    const jq = widget.jq;
    const id = jq?.attr("id") ?? "";
    if (id === targetId) {
      return findWidgetByVar(widgetVar, widgetClass);
    }
  }
  return undefined;
}

/**
 * Attempts to locate the widget for an element, given its widget variable.
 * @template {new (...args: never[]) => unknown} T Type of the widget to find.
 * @param {string} widgetVar Name of the widget variable of the widget to find.
 * @param {T} widgetClass Expected type of the widget. No check is performed if not given.
 * @returns {InstanceType<T> | undefined} The widget with the given widget variable.
 */
export function findWidgetByVar(widgetVar, widgetClass) {
  const widget = PrimeFaces.widgets[widgetVar];
  if (isNullish(widget) || widgetClass === undefined) {
    return undefined;
  }
  if (!isInstance(widget, widgetClass)) {
    console.error(`Widget for var '${widgetVar}' not of the correct class ${String(widgetClass)}`);
    return undefined;
  }
  return /**@type {InstanceType<T> | undefined}*/ (widget);
}

/**
 * Tries to find the widget for the given element. The following attempts are made:
 * - Search the list of all known widgets for a widget whose DOM element contains the
 * given element.
 * - Search for a widget with the same ID as the ID of the UI widget. The UI widget
 * is located by searching for the nearest parent with the class <code>ui-widget</code>
 * - Check whether containing UI widget has a data-widget-var attribute and search
 * for a widget with that widget variable.
 * @template {new (...args: never[]) => unknown} T Type of the widget to find.
 * @param {HTMLElement} el Element for which to find the widget instance.
 * @param {T} widgetClass Expected type of the widget. No check is performed if not given.
 * @returns {InstanceType<T> | undefined} The widget for the element, if found.
 */
export function findWidgetByElement(el, widgetClass) {
  const element = el;
  if (element === undefined) {
    return undefined;
  }

  // Try the DOM element
  for (const widget of recordValues(PrimeFaces.widgets)) {
    if (widget instanceof widgetClass) {
      const mainElement = widget.getJQ().get(0);
      if (mainElement !== undefined && (mainElement === element || mainElement.contains(element))) {
        return /** @type {InstanceType<T>} */ (widget);
      }
    }
  }

  // Find the main DOM element representing the widget container
  const selector = ".ui-widget,.ui-layout-container,.ui-inplace,.ui-calendar";
  const container = element.matches(selector) ? element : element.closest(selector);

  // Try to find the widget variable
  const widgetVar = container instanceof HTMLElement ? container.dataset.widgetVar : "";
  if (notEmpty(widgetVar)) {
    return findWidgetByVar(widgetVar, widgetClass);
  }

  // Try the ID
  return findWidgetById(container?.id ?? "", widgetClass);
}
